<template>
  <div class="admin-users-page">
    <!-- SearchBar -->
    <div class="search-section">
      <div class="search-container">
        <el-row :gutter="10">
          <el-col :span="6">
            <el-input
              placeholder="Search Username"
              v-model="search.username"
              @clear="handleSearch()"
              @keyup.enter.native="handleSearch()"
              clearable
              size="medium">
              <el-button slot="append" icon="el-icon-search" @click="handleSearch()"></el-button>
            </el-input>
          </el-col>
          <el-col :span="6">
            <el-input
              placeholder="Search Email"
              v-model="search.email"
              @clear="handleSearch()"
              @keyup.enter.native="handleSearch()"
              clearable
              size="medium">
              <el-button slot="append" icon="el-icon-search" @click="handleSearch()"></el-button>
            </el-input>
          </el-col>
          <el-col :span="6">
            <el-button type="default" @click="resetSearch()" size="medium">重置</el-button>
            <el-button type="primary" icon="el-icon-plus" @click="openAdd" size="medium">添加员工</el-button>
          </el-col>
        </el-row>
      </div>
    </div>

    <!-- Table -->
    <div class="table-container">
      <el-table
        :data="users"
        border
        stripe
        :header-cell-class-name="'headerBg'"
        class="custom-table"
        style="width: 100%"
        v-loading="loading"
      >
        <el-table-column type="index" label="#" width="40" align="center" />
        <el-table-column prop="username" label="用户名" align="center" />
        <el-table-column prop="email" label="邮箱" show-overflow-tooltip align="center" />
        <el-table-column prop="created_at" label="创建时间" width="160" align="center" />
        <el-table-column prop="updated_at" label="更新时间" width="160" align="center" />
        <el-table-column label="操作" width="180" align="center">
          <template slot-scope="scope">
            <div>
              <el-button type="success" size="small" @click="openEdit(scope.row)">编辑</el-button>
              <el-button type="danger" size="small" @click="handleDelete(scope.row)">删除</el-button>
            </div>
          </template>
        </el-table-column>
      </el-table>

      <!-- Pagination -->
      <div class="pagination-wrapper">
        <el-pagination
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :current-page="pageNum"
          :page-sizes="[10, 20, 30, 50, 100]"
          :page-size="pageSize"
          :layout="isMobile ? 'prev, pager, next' : 'total, sizes, prev, pager, next, jumper'"
          :total="total"
        />
      </div>
    </div>

    <!-- Add Dialog -->
    <el-dialog
      title="添加员工"
      :visible.sync="addDialogVisible"
      width="30%"
      :close-on-click-modal="false"
    >
      <el-form :model="addForm" :rules="addRules" ref="addForm" label-width="90px">
        <el-form-item label="用户名" prop="username">
          <el-input v-model="addForm.username" autocomplete="off" placeholder="Username" />
        </el-form-item>
        <el-form-item label="邮箱" prop="email">
          <el-input v-model="addForm.email" autocomplete="off" placeholder="Email" />
        </el-form-item>
        <el-form-item label="密码" prop="password">
          <el-input
            type="password"
            v-model="addForm.password"
            autocomplete="new-password"
            placeholder="至少6位字符"
            show-password
          />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="addDialogVisible = false">取 消</el-button>
        <el-button type="primary" :loading="saving" @click="submitAdd">确 定</el-button>
      </span>
    </el-dialog>

    <!-- Edit Dialog -->
    <el-dialog
      title="编辑员工"
      :visible.sync="editDialogVisible"
      width="30%"
      :close-on-click-modal="false"
    >
      <el-form :model="editForm" :rules="editRules" ref="editForm" label-width="90px">
        <el-form-item label="用户名" prop="username">
          <el-input v-model="editForm.username" autocomplete="off" />
        </el-form-item>
        <el-form-item label="邮箱" prop="email">
          <el-input v-model="editForm.email" autocomplete="off"/>
        </el-form-item>
        <el-form-item label="新密码" prop="password">
          <el-input
            type="password"
            v-model="editForm.password"
            autocomplete="new-password"
            placeholder="留空则不修改密码，至少6位字符"
            show-password
          />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="editDialogVisible = false">取 消</el-button>
        <el-button type="primary" :loading="saving" @click="submitEdit">保 存</el-button>
      </span>
    </el-dialog>

    <!-- Detail Dialog -->
    <el-dialog
      title="员工详情"
      :visible.sync="detailDialogVisible"
      width="600px"
      :close-on-click-modal="false"
    >
      <el-descriptions :column="1" border v-if="currentUser">
        <el-descriptions-item label="ID">{{ currentUser.id }}</el-descriptions-item>
        <el-descriptions-item label="用户名">{{ currentUser.username }}</el-descriptions-item>
        <el-descriptions-item label="邮箱">{{ currentUser.email }}</el-descriptions-item>
        <el-descriptions-item label="创建时间">{{ currentUser.created_at }}</el-descriptions-item>
        <el-descriptions-item label="更新时间">{{ currentUser.updated_at }}</el-descriptions-item>
      </el-descriptions>
      <span slot="footer">
        <el-button @click="detailDialogVisible = false">关 闭</el-button>
        <el-button type="primary" @click="editFromDetail">编 辑</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { formatDateTime } from '@/utils/timeUtil'

export default {
  name: 'AdminUsers',
  data() {
    const validateUsername = (rule, value, callback) => {
      if (!value) {
        callback(new Error('请输入用户名'))
        return
      }
      if (value.length < 3) {
        callback(new Error('用户名长度至少为3位'))
      } else {
        callback()
      }
    }
    const validatePassword = (rule, value, callback) => {
      if (!value) {
        callback(new Error('请输入密码'))
        return
      }
      if (value.length < 6) {
        callback(new Error('密码长度至少为6位'))
      } else {
        callback()
      }
    }
    const validatePasswordOptional = (rule, value, callback) => {
      // 编辑时密码可选，如果填写了则验证长度
      if (!value) {
        callback()
        return
      }
      if (value.length < 6) {
        callback(new Error('密码长度至少为6位'))
      } else {
        callback()
      }
    }
    const validateEmail = (rule, value, callback) => {
      if (!value) {
        callback(new Error('请输入邮箱地址'))
        return
      }
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!emailRegex.test(value)) {
        callback(new Error('邮箱格式不正确'))
      } else {
        callback()
      }
    }
    return {
      loading: false,
      saving: false,
      users: [],
      total: 0,
      pageNum: 1,
      pageSize: 20,
      search: {
        username: '',
        email: ''
      },
      addDialogVisible: false,
      addForm: {
        username: '',
        email: '',
        password: ''
      },
      addRules: {
        username: [{ validator: validateUsername, trigger: 'blur' }],
        email: [{ validator: validateEmail, trigger: 'blur' }],
        password: [{ validator: validatePassword, trigger: 'blur' }]
      },
      editDialogVisible: false,
      editForm: {
        id: null,
        username: '',
        email: '',
        password: ''
      },
      editRules: {
        username: [{ validator: validateUsername, trigger: 'blur' }],
        email: [{ validator: validateEmail, trigger: 'blur' }],
        password: [{ validator: validatePasswordOptional, trigger: 'blur' }]
      },
      detailDialogVisible: false,
      currentUser: null,
      isMobile: window.innerWidth <= 768
    }
  },
  created() {
    this.getUsers()
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
  },
  methods: {
    handleResize() {
      this.isMobile = window.innerWidth <= 768
    },
    handleSearch() {
      this.pageNum = 1
      this.getUsers()
    },
    resetSearch() {
      this.search.username = ''
      this.search.email = ''
      this.pageNum = 1
      this.getUsers()
    },
    handleSizeChange(size) {
      this.pageSize = size
      this.getUsers()
    },
    handleCurrentChange(page) {
      this.pageNum = page
      this.getUsers()
    },
    async getUsers() {
      this.loading = true
      try {
        const params = {
          pageNum: this.pageNum,
          pageSize: this.pageSize
        }
        if (this.search.username) {
          params.username = this.search.username
        }
        if (this.search.email) {
          params.email = this.search.email
        }
        const res = await this.request.get('/namzar/admin/adminUsers', { params })
        const list = Array.isArray(res?.data) ? res.data : []
        this.total = res.total || list.length
        this.users = list.map(item => ({
          id: item.id,
          username: item.username,
          email: item.email,
          created_at: item.created_at ? formatDateTime(item.created_at) : '--',
          updated_at: item.updated_at ? formatDateTime(item.updated_at) : '--'
        }))
      } catch (res) {
        this.$message.error(res?.message || '获取员工列表失败')
      } finally {
        this.loading = false
      }
    },
    openAdd() {
      this.addForm = {
        username: '',
        email: '',
        password: ''
      }
      this.addDialogVisible = true
      this.$nextTick(() => {
        this.$refs.addForm && this.$refs.addForm.clearValidate()
      })
    },
    async submitAdd() {
      this.$refs.addForm.validate(async valid => {
        if (!valid) return
        this.saving = true
        try {
          const res = await this.request.post('/namzar/admin/adminUsers/add', this.addForm)
          if (res && (res.code === 200 || res.message === '添加成功')) {
            this.$message.success('添加成功')
            this.addDialogVisible = false
            await this.getUsers()
          } else {
            this.$message.error(res.message || '添加失败')
          }
        } catch (res) {
          this.$message.error(res?.message || '添加失败')
        } finally {
          this.saving = false
        }
      })
    },
    openEdit(row) {
      this.editForm = {
        id: row.id,
        username: row.username,
        email: row.email,
        password: ''
      }
      this.editDialogVisible = true
      this.$nextTick(() => {
        this.$refs.editForm && this.$refs.editForm.clearValidate()
      })
    },
    async submitEdit() {
      this.$refs.editForm.validate(async valid => {
        if (!valid) return
        this.saving = true
        try {
          const payload = {
            id: this.editForm.id,
            username: this.editForm.username,
            email: this.editForm.email
          }
          // 只有填写了密码才添加到请求中
          if (this.editForm.password) {
            payload.password = this.editForm.password
          }
          const res = await this.request.post('/namzar/admin/adminUsers/edit', payload)
          if (res && (res.code === 200 || res.message === '更新成功')) {
            this.$message.success('更新成功')
            this.editDialogVisible = false
            await this.getUsers()
          } else {
            this.$message.error(res.message || '更新失败')
          }
        } catch (res) {
          this.$message.error(res?.message || '更新失败')
        } finally {
          this.saving = false
        }
      })
    },
    editFromDetail() {
      this.detailDialogVisible = false
      this.$nextTick(() => {
        this.openEdit(this.currentUser)
      })
    },
    handleDelete(row) {
      this.$confirm(`确定要删除员工 "${row.username}" 吗？`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      })
        .then(async () => {
          try {
            const res = await this.request.post('/namzar/admin/adminUsers/delete', { id: row.id })
            if (res && (res.code === 200 || res.message === '删除成功')) {
              this.$message.success('删除成功')
              await this.getUsers()
            } else {
              this.$message.error(res.message || '删除失败')
            }
          } catch (res) {
            this.$message.error(res?.message || '删除失败')
          }
        })
        .catch(res => {
          this.$message.error(res?.message || '删除失败')
        })
    }
  }
}
</script>

<style scoped>
.admin-users-page {
  padding: 20px;
  background-color: #f5f7fa;
  min-height: 100%;
}

.search-section {
  background: white;
  padding: 20px;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  margin-bottom: 20px;
}

.table-container {
  background: white;
  padding: 20px;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  overflow-x: auto;
}

.custom-table {
  border-radius: 8px;
  overflow: hidden;
}

.custom-table ::v-deep .el-table__header-wrapper {
  background-color: #f5f7fa;
}

.custom-table ::v-deep .el-table__header th {
  background-color: #f5f7fa;
  color: #606266;
  font-weight: 600;
}

.headerBg {
  background: #f5f7fa !important;
}

.pagination-wrapper {
  margin-top: 16px;
  text-align: center;
}

.pagination-wrapper ::v-deep .el-pagination {
  justify-content: center;
}
</style>

